import * as React from "react"
import { useEffect } from "react"
import { useSignal } from "@preact/signals-react"
import { Grid, TextField } from "@mui/material"
import { number, object, string } from "yup"
import { useAppToast, useValidatorSeeker } from "@ui-seeker/app"
import {
    AutocompleteAsyncSeeker,
    DatePickerSeeker,
    DrawerSeeker,
    SelectSeeker
} from "@ui-seeker/core"
import { isEmpty } from "@ui-seeker/utils"

import { DIPCountry } from "@components"
import { BACKEND_APIS } from "@constants"
import { customerSelectedID } from "@signals"
import { usePricingConfigDraft } from "../../@hooks"
import { CURRENCY_OPTIONS, PRICING_CONFIG_APIS, RRSP_MARKET_MAX_LENGTH } from "../../@constants"
import { rrspData, rrspFormFilters } from "../../@signals"
import { compactToDate, dateToCompact, windowsOverlap } from "../../@utils"


/** One RRSP row: SKU × market × validity window → price.
 *
 * Three things about the table drive this form, all verified on it:
 *
 * - `market` is `varchar(2)`, so the market list is **restricted to two characters** — `ES_CA`,
 *   which the retailer branch accepts, does not fit here and would be truncated on insert.
 * - `market` is stored **uppercase** (38.823/38.823 rows) while v1's market options return it
 *   lowercase, so it is upper-cased on the way out.
 * - `date_from`/`date_to` are compact `YYYYMMDD` integers, not dates (see `../../@utils`).
 *
 * The SKU selector is the one endpoint this screen needed from v2: v1's `sku/find` scopes by the
 * session user, so it cannot list another customer's catalogue.
 */
export default function RrspDrawer() {

    const { showToast } = useAppToast()
    const { stageRrspRow } = usePricingConfigDraft()
    const form = useSignal({})

    useEffect(() => {
        form.value = { ...rrspFormFilters.value }
    }, [rrspFormFilters.value])

    const isOpen = !isEmpty(rrspFormFilters.value)

    const validatorData = useValidatorSeeker({
        skuID: form.value?.skuID,
        market: form.value?.market,
        dateFrom: form.value?.dateFrom,
        dateTo: form.value?.dateTo,
        price: form.value?.price,
        currency: form.value?.currency
    }, object({
        skuID: number().required('One SKU must be selected').nullable(),
        market: string().required('One market must be selected').nullable(),
        dateFrom: number().required('A start date must be provided').nullable(),
        dateTo: number().required('An end date must be provided').nullable(),
        price: number().typeError('The price must be a number')
            .positive('The price must be greater than zero')
            .required('A price must be provided'),
        currency: string().required('One currency must be selected').nullable()
    }))

    /** Two rows of the same SKU and market must not both apply on the same day.
     *
     * An overlap does not fail on read: v2 resolves it by **lowest `id_rrsp`** (derived from the
     * ETL's own output), so the new row would silently never be used. Refusing it is clearer.
     */
    const findOverlap = () => rrspData.value.find(row =>
        row.rrspID !== form.value.rrspID
        && row.pending !== 'delete'
        && row.skuID === form.value.skuID
        && row.market === form.value.market?.toUpperCase()
        && windowsOverlap(row.dateFrom, row.dateTo, form.value.dateFrom, form.value.dateTo)
    )

    const handleConfirm = () => {
        if (form.value.dateFrom > form.value.dateTo)
            return showToast.warning('The end date cannot be earlier than the start date')

        const overlap = findOverlap()
        if (overlap)
            return showToast.warning(
                `This window overlaps row #${overlap.rrspID} of the same SKU and market`
            )

        stageRrspRow({
            ...form.value,
            market: form.value.market?.toUpperCase(),
            price: Number(form.value.price)
        })
        rrspFormFilters.value = {}
    }

    const drawerProps = {
        title: form.value?.isCreateMode ? 'Add an RRSP row' : `Edit RRSP #${form.value?.rrspID}`,
        isOpen,
        controller: () => rrspFormFilters.value = {},
        successProps: {
            onClick: isEmpty(validatorData.errors)
                ? handleConfirm
                : () => showToast.warning('Please ensure all mandatory fields are completed')
        }
    }

    return <DrawerSeeker {...drawerProps}>
        <Grid container spacing={2}>

            <Grid item xs={12}>
                <AutocompleteAsyncSeeker
                    urlAjax={PRICING_CONFIG_APIS.SKU_OPTIONS}
                    method="POST"
                    filters={{ customerID: customerSelectedID.value }}
                    label="SKU"
                    placeholder="Type to search a SKU..."
                    value={form.value?.skuID ?? null}
                    onChange={(e, option) => form.value = {
                        ...form.value, skuID: e.target.value, skuName: option?.label
                    }}
                    multiple={false}
                    sx={{ mt: 3, mb: 1 }}
                    error={isOpen && validatorData.errors?.skuID}
                />
            </Grid>

            <Grid item xs={12}>
                <DIPCountry
                    urlAjax={`${BACKEND_APIS.MASTER_DATA}/site/market-customer-options`}
                    filters={{ customerID: customerSelectedID.value }}
                    onAjax={options => options.filter(
                        option => String(option.value).length <= RRSP_MARKET_MAX_LENGTH
                    )}
                    value={form.value?.market?.toLowerCase() ?? null}
                    onChange={e => form.value = { ...form.value, market: e.target.value }}
                    multiple={false}
                    sx={{ mb: 1 }}
                    error={isOpen && validatorData.errors?.market}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <DatePickerSeeker
                    label="Valid from"
                    value={compactToDate(form.value?.dateFrom)}
                    onChange={date => form.value = { ...form.value, dateFrom: dateToCompact(date) }}
                    // `DatePickerSeeker` disables future dates by default (it exists for the
                    // dashboards' filters, which only look backwards). An RRSP window is a
                    // *forward-looking* validity: a price can start next month.
                    shouldDisableDate={() => false}
                    error={isOpen && validatorData.errors?.dateFrom}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <DatePickerSeeker
                    label="Valid to"
                    value={compactToDate(form.value?.dateTo)}
                    onChange={date => form.value = { ...form.value, dateTo: dateToCompact(date) }}
                    shouldDisableDate={() => false}
                    error={isOpen && validatorData.errors?.dateTo}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Price"
                    value={form.value?.price ?? ''}
                    onChange={e => form.value = { ...form.value, price: e.target.value }}
                    helperText={isOpen && validatorData.errors?.price}
                    error={isOpen && !!validatorData.errors?.price}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <SelectSeeker
                    options={CURRENCY_OPTIONS}
                    value={form.value?.currency ?? null}
                    onChange={e => form.value = { ...form.value, currency: e.target.value }}
                    label="Currency"
                    multiple={false}
                    error={isOpen && validatorData.errors?.currency}
                />
            </Grid>

        </Grid>
    </DrawerSeeker>
}
